import React, { useEffect, useState, useMemo, useRef, memo } from 'react';
import PaymentSenseConnectE from '../../../common/PaymentSenseConnectE';
import PaymentSenseWallet from '../../../common/PaymentSenseWallet';
import { venposService } from '../../../../services/venposService';
import { useBooking } from '../../../../contexts/BookingContext';
import {
  isAdultProductCode,
  isChildProductCode,
} from '../membershipUpgradeProductCodes';

// Utility function to check if JWT token is expired
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // Check if token is expired (exp is in seconds since epoch)
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {

    return true; // Assume expired if we can't parse it
  }
};

// Utility function to refresh payment session
const refreshPaymentSession = async (
  paymentSession,
  venposService,
  locationId,
  basketId,
  customerDetails,
  finalAmount,
  selectedTickets,
  selectedDate,
  pageId,
  modifierSelections,
  registeredCustomerId,
  appliedDiscounts,
  selectedTimeSlot,
  memberships
) => {

  try {
    const refreshedRequest = await venposService.createPaymentRequest(
      locationId,
      basketId,
      customerDetails,
      finalAmount,
      selectedTickets,
      selectedDate,
      pageId,
      modifierSelections || {},
      registeredCustomerId || null,
      appliedDiscounts || [],
      selectedTimeSlot || null,
      memberships || null
    );

    if (refreshedRequest.isError) {
      throw new Error(refreshedRequest.error || 'Failed to refresh payment session');
    }

    return {
      orderId: refreshedRequest.data.orderId,
      amount: finalAmount,
      currency: 'GBP',
      paymentSenseCheckoutUrl: refreshedRequest.data.paymentSenseCheckoutUrl,
      paymentSenseJWT: refreshedRequest.data.paymentSenseJWT,
      paymentSenseJWT_ALT: refreshedRequest.data.paymentSenseJWT_ALT,
      paymentSenseMerchantUrl: refreshedRequest.data.paymentSenseMerchantUrl,
      paymentSenseMerchantUrl_ALT: refreshedRequest.data.paymentSenseMerchantUrl_ALT,
      paymentSense_ApiUrl: refreshedRequest.data.paymentSense_ApiUrl
    };
  } catch (error) {

    throw error;
  }
};

// Component to show token status and refresh if needed
const TokenStatusIndicator = ({ paymentSession }) => {
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(null);

  useEffect(() => {
    const updateTimeUntilExpiry = () => {
      if (!paymentSession?.paymentSenseJWT) {
        setTimeUntilExpiry(null);
        return;
      }

      try {
        const parts = paymentSession.paymentSenseJWT.split('.');
        if (parts.length !== 3) {
          setTimeUntilExpiry(null);
          return;
        }

        const payload = JSON.parse(atob(parts[1]));
        const expiryTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeLeft = expiryTime - currentTime;

        if (timeLeft <= 0) {
          setTimeUntilExpiry(0);
        } else {
          setTimeUntilExpiry(Math.floor(timeLeft / 1000)); // Convert to seconds
        }
      } catch (error) {
        setTimeUntilExpiry(null);
      }
    };

    updateTimeUntilExpiry();
    const interval = setInterval(updateTimeUntilExpiry, 1000);

    return () => clearInterval(interval);
  }, [paymentSession?.paymentSenseJWT]);

  if (!timeUntilExpiry || timeUntilExpiry > 300) { // Only show if expires in 5 minutes or less
    return null;
  }

  const minutes = Math.floor(timeUntilExpiry / 60);
  const seconds = timeUntilExpiry % 60;

  return (
    <div style={{
      padding: '0.5rem',
      background: timeUntilExpiry < 60 ? '#fff3cd' : '#e7f3ff',
      border: `1px solid ${timeUntilExpiry < 60 ? '#ffeaa7' : '#b3d9ff'}`,
      borderRadius: '4px',
      marginBottom: '1rem',
      fontSize: '0.85rem',
      color: timeUntilExpiry < 60 ? '#856404' : '#004085',
      textAlign: 'center'
    }}>
      {timeUntilExpiry < 60 ? '⚠️' : '⏰'} Token expires in {minutes}:{seconds.toString().padStart(2, '0')}
    </div>
  );
};

const PaymentPage = ({
  basketId,
  locationId,
  customerDetails,
  venposBasket,
  selectedTickets,
  selectedDate,
  pageId,
  modifierSelections,
  registeredCustomerId,
  onPaymentProcessing,
  onPaymentSuccess,
  onPaymentFailure,
  onCancel,
  upgradeCreditAmount, // Credit amount from original booking
  sortedUpgradeItems, // Sorted items from original booking (lowest first)
  upgradeFromBooking, // Original booking reference for upgrade journey
  upgradeDiscountApplied = false, // For future display/logging
  upgradeDiscountCode = null,
  upgradeDiscountMessage = '',
  upgradeDiscountValue = 0,
  membershipDetails = null
}) => {
  const [errors, setErrors] = useState({});
  const [paymentSession, setPaymentSession] = useState(null);
  const [initializingPayment, setInitializingPayment] = useState(false);
  const [paymentInitialized, setPaymentInitialized] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);
  
  // Use ref to prevent multiple simultaneous initializations
  const initializationInProgressRef = useRef(false);

  // Get selectedTimeSlot from booking context for event metadata
  const { selectedTimeSlot, getSubtotal } = useBooking();

  const basketData = venposBasket?.data ? venposBasket.data : (venposBasket || null);
  
  // ✅ CRITICAL: Check if basket has upgrade discount already applied
  // If so, we need to use the pre-discount total to avoid double-discounting
  const basketDiscounts = basketData?.discounts || [];
  const hasUpgradeDiscountInBasket = basketDiscounts.some(d => {
    const code = d.code || d.discountCode || d;
    return typeof code === 'string' && (code.startsWith('System1-') || code.startsWith('UPGRADE-'));
  });
  
  console.log('💳 [PaymentPage] Basket discount check:', {
    basketDiscounts: basketDiscounts.map(d => d.code || d.discountCode || d),
    hasUpgradeDiscountInBasket,
    basketTotalPrice: basketData?.totalPrice,
    basketFinalPrice: basketData?.finalPrice
  });
  
  // If upgrade discount is in basket, totalPrice might already be discounted
  // We need the PRE-discount total to calculate correctly
  const baseSubtotal = basketData?.totalPrice ?? getSubtotal();
  
  // Calculate applied upgrade credit client-side (1 discount per membership)
  // Match adult codes to adult memberships, child codes to child memberships
  const calculateAppliedUpgradeCredit = () => {
    if (!upgradeCreditAmount || !sortedUpgradeItems) {
      return 0;
    }
    
    // Helper functions to determine membership type
    const isChildMembership = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      
      // Exclude free child tickets (under 3, under 2, infant) - these don't count as child memberships
      if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
          description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
        return false;
      }
      
      return name.includes('child') || 
             name.includes('junior') || 
             description.includes('child') || 
             description.includes('junior');
    };
    const isFamilyMembership = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      return name.includes('family') || description.includes('family');
    };
    
    // Helper function to check if ticket is a free "under 3" ticket (should not count for any discounts)
    const isFreeUnder3Ticket = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
             description.includes('under 3') || description.includes('under 2') || description.includes('infant');
    };
    
    const isAdultOrConcessionItem = (item) => isAdultProductCode(item.productCode);
    
    // Get available items (handle both array format for backward compatibility and object format)
    const availableChildItems = Array.isArray(sortedUpgradeItems) 
      ? sortedUpgradeItems.filter(item => isChildProductCode(item.productCode))
      : (sortedUpgradeItems.child || []);
    
    const availableAdultItems = Array.isArray(sortedUpgradeItems)
      ? sortedUpgradeItems.filter(item => isAdultOrConcessionItem(item))
      : (sortedUpgradeItems.adult || []);
    
    if (availableChildItems.length === 0 && availableAdultItems.length === 0) {
      return 0;
    }
    
    // Separate memberships by type
    const childMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && isChildMembership(ticket) && !isFamilyMembership(ticket)
    );
    const adultMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && !isChildMembership(ticket) && !isFamilyMembership(ticket) && !isFreeUnder3Ticket(ticket)
    );
    const familyMemberships = selectedTickets.filter(ticket =>
      ticket.webSaleItemTypeID === 4 && isFamilyMembership(ticket)
    );
    
    const childMembershipCount = childMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    const adultMembershipCount = adultMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    const familyMembershipCount = familyMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    
    // Family membership can consume up to 2 adult + 3 child credits per membership
    const totalAdultNeeded = adultMembershipCount + (familyMembershipCount * 2);
    const totalChildNeeded = childMembershipCount + (familyMembershipCount * 3);
    
    if (childMembershipCount === 0 && adultMembershipCount === 0) {
      return 0;
    }
    
    // Match child items to child memberships
    const childItemsToUse = availableChildItems.slice(0, totalChildNeeded);
    const childCredit = childItemsToUse.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);
    
    // Match adult items to adult memberships
    const adultItemsToUse = availableAdultItems.slice(0, totalAdultNeeded);
    const adultCredit = adultItemsToUse.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);
    
    return childCredit + adultCredit;
  };
  const computedUpgradeCredit = calculateAppliedUpgradeCredit();
  
  console.log('💳 [PaymentPage] DETAILED Price Breakdown:', {
    step1_baseSubtotal: baseSubtotal,
    step2_basketDataTotalPrice: basketData?.totalPrice,
    step3_upgradeDiscountValue: upgradeDiscountValue,
    step4_computedUpgradeCredit: computedUpgradeCredit,
    step5_upgradeCreditAmount: upgradeCreditAmount,
    step6_sortedUpgradeItems: sortedUpgradeItems ? 'exists' : 'null',
    step7_selectedTicketsCount: selectedTickets.length,
    upgradeDiscountCode: upgradeDiscountCode
  });
  
  const appliedUpgradeCredit = upgradeDiscountValue > 0 ? upgradeDiscountValue : computedUpgradeCredit;
  
  // Calculate total discount amount from basket discounts (excluding upgrade credits which are handled separately)
  const basketDiscountAmount = basketData?.discounts?.reduce((sum, discount) => {
    const code = (discount.code || discount.discountCode || '').toString().toUpperCase();
    // Exclude upgrade discounts as they're handled separately
    if (code.startsWith('UPGRADE-') || code.startsWith('SYSTEM1-')) {
      return sum;
    }
    return sum + (discount.discountedAmount || 0);
  }, 0) || 0;
  
  // Calculate final amount: subtotal - upgrade credit - other discounts
  const calculatedFinalAmount = Math.max(0, baseSubtotal - appliedUpgradeCredit - basketDiscountAmount);
  
  // Prefer basket finalPrice when present (including £0 after 100% discount).
  const basketFinalPrice = basketData?.finalPrice;
  const parsedBasketFinal = Number(basketFinalPrice);
  const hasExplicitBasketFinal =
    basketFinalPrice !== null &&
    basketFinalPrice !== undefined &&
    Number.isFinite(parsedBasketFinal);
  const finalAmount = hasExplicitBasketFinal
    ? Math.max(0, parsedBasketFinal)
    : basketData != null
      ? Math.max(0, calculatedFinalAmount)
      : Math.max(0, calculatedFinalAmount > 0 ? calculatedFinalAmount : 0);
  
  console.log('💳 [PaymentPage] FINAL Price calculation:', {
    baseSubtotal,
    computedUpgradeCredit,
    upgradeDiscountValue,
    appliedUpgradeCredit,
    basketDiscountAmount,
    calculatedFinalAmount,
    basketFinalPrice: basketData?.finalPrice,
    finalAmountUsed: finalAmount,
    discounts: basketData?.discounts?.map(d => ({ code: d.code || d.discountCode, amount: d.discountedAmount }))
  });

  // Memoize applied discounts to prevent unnecessary re-initialization
  // Use JSON.stringify to create stable dependency on discount codes only
  const discountCodesString = useMemo(() => {
    const codes = (venposBasket?.data?.discounts || [])
      .map(d => d.code || d.discountCode || d)
      .filter(Boolean);
    
    // ✅ CRITICAL: Include upgrade discount codes (System1- prefix) from basket
    // The dynamic discount code is created in BookingJourney.js and should be in the basket
    // Check for System1 codes in the basket discounts
    const system1Codes = codes.filter(code => 
      typeof code === 'string' && code.startsWith('System1-')
    );
    
    // If we have upgrade items but no System1 code in basket, log a warning
    // (This shouldn't happen as the code is created before basket load)
    if (sortedUpgradeItems && upgradeFromBooking && system1Codes.length === 0) {
      console.warn('⚠️ [PaymentPage] Upgrade journey detected but no System1 discount code found in basket');
      console.warn('⚠️ [PaymentPage] Basket discounts:', codes);
    }
    
    // Include any System1 codes found (they should already be in codes array from basket)
    // No need to generate old UPGRADE- codes - we now use a single dynamic System1 code
    
    return JSON.stringify(codes);
  }, [venposBasket?.data?.discounts, sortedUpgradeItems, upgradeFromBooking, selectedTickets]);

  const appliedDiscounts = useMemo(() => {
    const codes = JSON.parse(discountCodesString);
    console.log('💳 [PaymentPage] Applied discounts (including upgrade codes):', codes);
    return codes;
  }, [discountCodesString]);

  const membershipsArray = useMemo(() => {
    if (!membershipDetails || !Array.isArray(membershipDetails) || membershipDetails.length === 0) {
      console.log('💳 [PaymentPage] No membershipDetails provided, membershipsArray will be null');
      return null;
    }

    console.log('💳 [PaymentPage] Building membershipsArray from membershipDetails:', membershipDetails);
    const fallbackCustomer = customerDetails || {};
    const fallbackAddress = fallbackCustomer.address || {};

    const buildMemberEntry = (memberDetail = {}, isPrimaryContact = false, memberIndex = 0) => {
      // Helper to check if a value exists (not null, undefined, or empty string)
      const hasValue = (val) => val !== null && val !== undefined && val !== '';
      
      // CRITICAL: For firstName and lastName, read DIRECTLY from memberDetail
      // These come from the membership form, NOT from billing details
      // If they're empty, that's fine - don't fallback to billing
      const firstName = memberDetail.firstName || '';
      const lastName = memberDetail.lastName || '';
      
      console.log('💳 [PaymentPage] buildMemberEntry - Name fields (DIRECT from memberDetail):', {
        'memberDetail.firstName (raw)': memberDetail.firstName,
        'memberDetail.lastName (raw)': memberDetail.lastName,
        'firstName (final)': firstName,
        'lastName (final)': lastName,
        'memberDetail object': memberDetail,
        'fallbackCustomer.firstName': fallbackCustomer.firstName,
        'fallbackCustomer.lastName': fallbackCustomer.lastName,
        'WILL USE MEMBERSHIP FORM DATA (not billing)': true,
        'isPrimaryContact': isPrimaryContact,
        'memberIndex': memberIndex
      });
      
      // CRITICAL: If the member name differs from the billing customer name,
      // don't link to the customer account (set webCustomerId to null)
      // This ensures Venpos creates a new member profile with the correct name
      const billingFirstName = fallbackCustomer.firstName || '';
      const billingLastName = fallbackCustomer.lastName || '';
      const namesMatch = (firstName.toLowerCase() === billingFirstName.toLowerCase() && 
                          lastName.toLowerCase() === billingLastName.toLowerCase());
      
      // CRITICAL: For family memberships, only the primary contact (first member) should be linked to registered customer
      // All other members should have webCustomerId: null to create new member profiles
      let webCustomerId = null;
      if (isPrimaryContact) {
        // Primary contact: link to registered customer if names match
        webCustomerId = (namesMatch && registeredCustomerId) 
          ? registeredCustomerId 
          : (hasValue(memberDetail.webCustomerId) ? memberDetail.webCustomerId : null);
      } else {
        // Other members: always null to create new member profiles (unless explicitly set in memberDetail)
        webCustomerId = hasValue(memberDetail.webCustomerId) ? memberDetail.webCustomerId : null;
      }
      
      console.log('💳 [PaymentPage] Member name vs billing name:', {
        memberFirstName: firstName,
        memberLastName: lastName,
        billingFirstName: billingFirstName,
        billingLastName: billingLastName,
        namesMatch: namesMatch,
        webCustomerId: webCustomerId,
        willCreateNewMember: !namesMatch
      });
      
      const useBillingAddress = memberDetail.useBillingAddress !== false; // default true
      console.log('💳 [PaymentPage] buildMemberEntry - useBillingAddress:', useBillingAddress);
      console.log('💳 [PaymentPage] fallbackAddress:', fallbackAddress);
      console.log('💳 [PaymentPage] memberDetail.useBillingAddress:', memberDetail.useBillingAddress);
      
      // Normalize country codes: UK -> GB, United Kingdom -> GB
      const normalizeCountry = (country) => {
        if (!country) return 'GB';
        if (country === 'UK' || country === 'United Kingdom') return 'GB';
        return country;
      };
      
      const resolvedAddress = useBillingAddress ? {
        houseNameNumber: fallbackAddress.houseNameNumber || fallbackAddress.houseNumber || '',
        addressFirstLine: fallbackAddress.line1 || fallbackAddress.addressFirstLine || '',
        addressSecondLine: fallbackAddress.line2 || fallbackAddress.addressSecondLine || '',
        cityTown: fallbackAddress.city || fallbackAddress.cityTown || '',
        postCode: fallbackAddress.postcode || fallbackAddress.postCode || '',
        selectedCountry: normalizeCountry(fallbackAddress.country)
      } : {
        houseNameNumber: hasValue(memberDetail.houseNameNumber) ? memberDetail.houseNameNumber : '',
        addressFirstLine: hasValue(memberDetail.addressFirstLine) ? memberDetail.addressFirstLine : '',
        addressSecondLine: hasValue(memberDetail.addressSecondLine) ? memberDetail.addressSecondLine : '',
        cityTown: hasValue(memberDetail.cityTown) ? memberDetail.cityTown : '',
        postCode: hasValue(memberDetail.postCode) ? memberDetail.postCode : '',
        selectedCountry: normalizeCountry(hasValue(memberDetail.selectedCountry) ? memberDetail.selectedCountry : memberDetail.country)
      };
      console.log('💳 [PaymentPage] resolvedAddress:', resolvedAddress);

      // For primary contact (first member in family membership), ensure contact details are populated
      const resolvedMobile = isPrimaryContact 
        ? (hasValue(memberDetail.mobile) ? memberDetail.mobile : (fallbackCustomer.phone || fallbackCustomer.mobile || ''))
        : (hasValue(memberDetail.mobile) ? memberDetail.mobile : '');
      const resolvedEmail = isPrimaryContact
        ? (hasValue(memberDetail.email) ? memberDetail.email : (fallbackCustomer.email || ''))
        : (hasValue(memberDetail.email) ? memberDetail.email : '');
      const resolvedTelephone = isPrimaryContact
        ? (hasValue(memberDetail.telephone) ? memberDetail.telephone : (fallbackCustomer.telephone || ''))
        : (hasValue(memberDetail.telephone) ? memberDetail.telephone : '');

      return {
        webCustomerId: webCustomerId,
        // CRITICAL: Use membership form data DIRECTLY - no fallback to billing for names
        firstName: firstName, // Direct from membership form
        lastName: lastName, // Direct from membership form
        selectedTitle: hasValue(memberDetail.selectedTitle) ? memberDetail.selectedTitle : (fallbackCustomer.selectedTitle || 1),
        mobile: resolvedMobile,
        telephone: resolvedTelephone,
        email: resolvedEmail,
        selectedCountry: normalizeCountry(resolvedAddress.selectedCountry || resolvedAddress.country),
        postCode: resolvedAddress.postCode || resolvedAddress.postcode || '',
        cityTown: resolvedAddress.cityTown || resolvedAddress.city || '',
        addressFirstLine: resolvedAddress.addressFirstLine || resolvedAddress.line1 || '',
        addressSecondLine: resolvedAddress.addressSecondLine || resolvedAddress.line2 || '',
        houseNameNumber: resolvedAddress.houseNameNumber || resolvedAddress.houseNumber || '',
        contactable: memberDetail.contactable !== undefined ? memberDetail.contactable : (fallbackCustomer.contactable ?? false),
        contactableByEmail: memberDetail.contactableByEmail !== undefined ? memberDetail.contactableByEmail : (fallbackCustomer.contactable ?? false),
        contactableBySMS: memberDetail.contactableBySMS ?? false,
        contactableByPost: memberDetail.contactableByPost ?? false,
        contactableByPhone: memberDetail.contactableByPhone ?? false,
        contactableByThirdParty: memberDetail.contactableByThirdParty ?? false,
        // CRITICAL: customerCount must be memberIndex + 1 (1, 2, 3, 4, 5 for a 5-member family)
        customerCount: memberIndex + 1,
        // CRITICAL: cardRequired should be true ONLY for primary contact (first member), false for others
        cardRequired: isPrimaryContact
      };
    };

    return membershipDetails.map((membership, index) => {
      const memberEntries = Array.isArray(membership.memberDetails) && membership.memberDetails.length > 0
        ? membership.memberDetails.map((detail, memberIndex) => {
            console.log('💳 [PaymentPage] Building member entry from detail:', {
              detail: detail,
              hasFirstName: detail.firstName,
              hasLastName: detail.lastName,
              fallbackFirstName: fallbackCustomer.firstName,
              fallbackLastName: fallbackCustomer.lastName,
              memberIndex: memberIndex,
              isPrimaryContact: memberIndex === 0
            });
            return buildMemberEntry(detail, memberIndex === 0, memberIndex);
          })
        : [buildMemberEntry({}, true, 0)];

      const finalMemberCount = membership.memberCount || memberEntries.length || 1;
      console.log('💳 [PaymentPage] Building membership for payment:', {
        membershipTypeId: membership.membershipTypeId,
        originalMemberCount: membership.memberCount,
        memberEntriesLength: memberEntries.length,
        finalMemberCount: finalMemberCount,
        memberNames: memberEntries.map(m => `${m.firstName} ${m.lastName}`)
      });
      
      const result = {
        ...membership,
        index: membership.index ?? index,
        memberCount: finalMemberCount,
        memberDetails: memberEntries
      };
      
      console.log('💳 [PaymentPage] Built membership entry:', {
        membershipTypeId: result.membershipTypeId,
        memberDetails: result.memberDetails.map(m => ({
          firstName: m.firstName,
          lastName: m.lastName,
          email: m.email
        }))
      });
      
      return result;
    });
  }, [membershipDetails, customerDetails, registeredCustomerId]);

  // Log when membershipsArray changes
  useEffect(() => {
    if (membershipsArray) {
      console.log('💳 [PaymentPage] membershipsArray built:', {
        count: membershipsArray.length,
        memberships: membershipsArray.map(m => ({
          membershipTypeId: m.membershipTypeId,
          memberCount: m.memberCount,
          memberDetailsCount: m.memberDetails?.length || 0,
          firstMember: m.memberDetails?.[0] ? {
            firstName: m.memberDetails[0].firstName,
            lastName: m.memberDetails[0].lastName,
            email: m.memberDetails[0].email
          } : null
        }))
      });
    }
  }, [membershipsArray]);

  // Check and refresh expired payment session
  const ensureValidPaymentSession = async () => {
    if (!paymentSession) return paymentSession;

    // Check if token is expired
    if (isTokenExpired(paymentSession.paymentSenseJWT)) {

      setRefreshingToken(true);

      try {
        const refreshedSession = await refreshPaymentSession(
          paymentSession,
          venposService,
          locationId,
          basketId,
          customerDetails,
          finalAmount,
          selectedTickets,
          selectedDate,
          pageId,
          modifierSelections,
          registeredCustomerId,
          appliedDiscounts,
          selectedTimeSlot,
          membershipsArray
        );

        setPaymentSession(refreshedSession);
        return refreshedSession;
      } catch (error) {

        setErrors({ payment: 'Payment session expired. Please refresh and try again.' });
        return null;
      } finally {
        setRefreshingToken(false);
      }
    }

    return paymentSession;
  };

  // Reset payment initialization when basketId changes (indicates fresh basket after cancel/back)
  useEffect(() => {

    setPaymentInitialized(false);
    setPaymentSession(null);
    setErrors({});
  }, [basketId]);
  
  // ✅ NEW: Manual initialization function (doesn't auto-run on mount)
  const initializePayment = async () => {
    // Prevent multiple simultaneous initializations using ref
    if (initializationInProgressRef.current || paymentInitialized || initializingPayment || paymentSession) {
      console.log('⚠️ [PaymentPage] Payment already initialized or in progress');
      return;
    }
    
    // Mark initialization as in progress
    initializationInProgressRef.current = true;
    
    // Validate required data
    if (!basketId || !locationId || !customerDetails) {
      console.error('❌ [PaymentPage] Missing required data for payment initialization');
      setErrors({ payment: 'Missing required booking data. Please go back and try again.' });
      initializationInProgressRef.current = false;
      return;
    }
    
    // Ensure customerDetails has required fields
    if (!customerDetails.email || !customerDetails.firstName || !customerDetails.lastName) {
      console.error('❌ [PaymentPage] Missing customer details');
      setErrors({ payment: 'Missing customer information. Please go back and complete your details.' });
      initializationInProgressRef.current = false;
      return;
    }
    
    if (!(finalAmount > 0)) {
      console.error('❌ [PaymentPage] Invalid amount:', finalAmount);
      setErrors({ payment: 'Invalid payment amount. Please go back and try again.' });
      initializationInProgressRef.current = false;
      return;
    }

    // Double-check that Connect-E hasn't already been initialized
    const paymentContainer = document.getElementById('payment-form-container');
    if (paymentContainer?.hasChildNodes()) {
      console.log('✅ [PaymentPage] Payment form already initialized');
      setPaymentInitialized(true);
      initializationInProgressRef.current = false;
      return;
    }

    setInitializingPayment(true);
    console.log('💳 [PaymentPage] Initializing payment (this will create order in Venpos)...');
    
    try {
      // ⚠️ IMPORTANT: This creates the order in Venpos BEFORE payment is completed
      // This is required by Venpos API to generate a payment token
      // The order should remain "pending" until payment succeeds
      const paymentRequest = await venposService.createPaymentRequest(
        locationId,
        basketId,
        customerDetails,
        finalAmount,
        selectedTickets,
        selectedDate,
        pageId,
        modifierSelections || {},
        registeredCustomerId || null,
        appliedDiscounts || [],
        selectedTimeSlot || null,
        membershipsArray || null
      );

      if (paymentRequest.isError) {
        setErrors({ payment: paymentRequest.error || 'Payment initialization failed. Click below to retry.' });
        setInitializingPayment(false);
        setPaymentInitialized(false); // Allow retry
        initializationInProgressRef.current = false;
        return;
      }

      const session = {
        orderId: paymentRequest.data.orderId,
        amount: finalAmount,
        currency: 'GBP',
        paymentSenseCheckoutUrl: paymentRequest.data.paymentSenseCheckoutUrl,
        paymentSenseJWT: paymentRequest.data.paymentSenseJWT,
        paymentSenseJWT_ALT: paymentRequest.data.paymentSenseJWT_ALT,
        paymentSenseMerchantUrl: paymentRequest.data.paymentSenseMerchantUrl,
        paymentSenseMerchantUrl_ALT: paymentRequest.data.paymentSenseMerchantUrl_ALT,
        paymentSense_ApiUrl: paymentRequest.data.paymentSense_ApiUrl
      };

      console.log('✅ [PaymentPage] Payment session created:', {
        hasOrderId: !!session.orderId,
        hasCheckoutUrl: !!session.paymentSenseCheckoutUrl,
        hasJWT: !!session.paymentSenseJWT
      });

      console.log('💳 [PaymentPage] Setting payment session in state');
      setPaymentSession(session);
      setPaymentInitialized(true);
      setInitializingPayment(false);
      initializationInProgressRef.current = false;
      console.log('✅ [PaymentPage] Payment initialization complete');

      // Check token validity after a delay to ensure user has time to fill form
      setTimeout(() => {
        ensureValidPaymentSession().then(validSession => {
          if (validSession && validSession !== session) {
            setPaymentSession(validSession);
          }
        }).catch(error => {
          console.error('Token validation error:', error);
        });
      }, 1000);
    } catch (e) {
      console.error('Payment initialization error:', e);
      setErrors({ payment: 'Failed to initialize payment. Click below to retry.' });
      setInitializingPayment(false);
      setPaymentInitialized(false); // Allow retry
      initializationInProgressRef.current = false;
    }
  };

  // ✅ IMPORTANT: Auto-initialize payment on mount
  // This creates the order in Venpos immediately to get the payment token
  // The order should remain "pending/unpaid" until payment succeeds
  useEffect(() => {
    let isMounted = true;
    let initTimeout = null;

    const autoInit = async () => {
      // Wait a bit for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (!isMounted) return;
      
      // Auto-initialize payment
      await initializePayment();
    };

    // Only auto-initialize if not already initialized
    if (!initializationInProgressRef.current && !paymentInitialized && !initializingPayment && !paymentSession) {
      autoInit();
    }

    // Cleanup function
    return () => {
      isMounted = false;
      initializationInProgressRef.current = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
    };
  }, [basketId, locationId, customerDetails, finalAmount, selectedTickets, selectedDate, pageId, modifierSelections, registeredCustomerId, appliedDiscounts, selectedTimeSlot, membershipsArray]);

  return (
    <div style={{ 
      maxWidth: 1000, 
      margin: '0 auto',
      padding: '0 0.5rem'
    }}>
      <h2 style={{ 
        marginBottom: '1rem',
        fontSize: 'clamp(1.5rem, 4vw, 1.8rem)'
      }}>Secure Payment</h2>

      {upgradeDiscountMessage && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          borderRadius: 8,
          border: `1px solid ${upgradeDiscountApplied ? '#2e7d32' : '#fb8c00'}`,
          background: upgradeDiscountApplied ? 'rgba(200,230,201,0.6)' : 'rgba(255,224,178,0.6)',
          color: upgradeDiscountApplied ? '#1b5e20' : '#bf360c',
          fontSize: '0.9rem',
          fontWeight: 600
        }}>
          {upgradeDiscountMessage}
        </div>
      )}
      <div style={{ 
        marginBottom: '1rem', 
        color: '#2c3e50',
        fontSize: 'clamp(1rem, 3vw, 1.1rem)'
      }}>
        Amount due: <strong>£{Number(finalAmount || 0).toFixed(2)}</strong>
      </div>

      {errors.payment && (
        <div style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: 8, 
          marginBottom: '1rem',
          fontSize: '0.95rem',
          lineHeight: '1.5'
        }}>
          {errors.payment}
        </div>
      )}

      {initializingPayment && (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center', 
          color: '#666' 
        }}>
          <div style={{ 
            marginBottom: '1rem',
            fontSize: 'clamp(1rem, 3vw, 1.1rem)'
          }}>🔄 Initializing secure payment...</div>
          <div style={{ fontSize: '0.9rem' }}>Please wait while we set up your payment session.</div>
        </div>
      )}

      {paymentSession && !initializingPayment && !refreshingToken && (
        <>
          {console.log('🎨 [PaymentPage] Rendering PaymentSenseConnectE with session:', {
            hasSession: !!paymentSession,
            hasCheckoutUrl: !!paymentSession?.paymentSenseCheckoutUrl,
            hasJWT: !!paymentSession?.paymentSenseJWT
          })}
          <PaymentSenseConnectE
            paymentSession={paymentSession}
            onPaymentProcessing={onPaymentProcessing}
            onPaymentSuccess={onPaymentSuccess}
            onPaymentFailure={onPaymentFailure}
            onPaymentCancel={onCancel}
          />
          
          {/* TEMPORARILY DISABLED - Waiting for Apple Pay/Google Pay approval */}
          {/* <PaymentSenseWallet
            paymentSession={paymentSession}
            customerDetails={customerDetails}
            onPaymentProcessing={onPaymentProcessing}
            onPaymentSuccess={onPaymentSuccess}
            onPaymentFailure={onPaymentFailure}
            onPaymentCancel={onCancel}
          /> */}
          
        </>
      )}

      {paymentSession && !refreshingToken && (
        <TokenStatusIndicator paymentSession={paymentSession} />
      )}

      {refreshingToken && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          <div style={{ marginBottom: '1rem' }}>🔄 Refreshing payment session...</div>
          <div style={{ fontSize: '0.9rem' }}>Please wait while we update your secure payment token.</div>
        </div>
      )}

      {!paymentSession && !initializingPayment && errors.payment && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div style={{ 
            background: '#fff3cd', 
            color: '#856404', 
            padding: '1rem', 
            borderRadius: '8px', 
            marginBottom: '1rem',
            border: '1px solid #ffeaa7'
          }}>
            ⚠️ {errors.payment}
          </div>
          <button
            onClick={() => {
              setErrors({});
              setPaymentInitialized(false);
              setInitializingPayment(false);
              initializationInProgressRef.current = false;
              // Retry initialization
              initializePayment();
            }}
            style={{
              padding: '1rem 2rem',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            🔄 Retry Payment Initialization
          </button>
        </div>
      )}

      {!paymentSession && !initializingPayment && !errors.payment && (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
          Payment session not ready.
        </div>
      )}
    </div>
  );
};

export default memo(PaymentPage);


